import { useState, useMemo, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ChevronLeft, ChevronRight, ChevronDown, CalendarDays, Trash2, Image, Film, Smartphone, Youtube } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClearPublished } from '@/api/hooks/usePoster'
import { toast } from 'sonner'
import type { ScheduledPost, Account } from '@/types'
import type { YouTubeChannel } from '@/api/hooks/useYouTube'
import { PostCard } from './PostCard'

interface CalendarViewProps {
  posts: ScheduledPost[]
  accounts: Account[]
  ytChannels: YouTubeChannel[]
  onEdit?: (post: ScheduledPost) => void
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']

export function CalendarView({ posts, accounts, ytChannels, onEdit }: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [accountFilter, setAccountFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState('all')
  const [pageSize, setPageSize] = useState(4)
  const [currentPage, setCurrentPage] = useState(0)
  const clearPublished = useClearPublished()
  const [clearOpen, setClearOpen] = useState(false)
  const [clearStatuses, setClearStatuses] = useState<Record<string, boolean>>({ published: true, failed: true, pending: true })
  const [clearTypes, setClearTypes] = useState<Record<string, boolean>>({ photo: true, clip: true, long_video: true, story: true })
  const clearRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clearRef.current && !clearRef.current.contains(e.target as Node)) setClearOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const getPostContentType = (p: ScheduledPost): string => {
    if (p.postType === 'story') return 'story'
    if (p.mediaType === 'video' && p.campaignId) return 'clip'
    if (p.mediaType === 'video') return 'long_video'
    return 'photo'
  }

  const getPostStatus = (p: ScheduledPost): string => {
    if (p.status === 'failed') return 'failed'
    if (p.status === 'published' || p.published) return 'published'
    return 'pending'
  }

  const clearCounts = useMemo(() => {
    const counts = { byStatus: { published: 0, failed: 0, pending: 0 }, byType: { photo: 0, clip: 0, long_video: 0, story: 0 }, total: 0 }
    posts.forEach(p => {
      counts.byStatus[getPostStatus(p) as keyof typeof counts.byStatus]++
      counts.byType[getPostContentType(p) as keyof typeof counts.byType]++
      counts.total++
    })
    return counts
  }, [posts])

  const accountOptions = useMemo(() => {
    const map = new Map<string, string>()
    posts.forEach(p => {
      const key = p.platform === 'yt' ? `yt_${p.youtubeChannelId}` : p.pageId
      if (key && p.accountName) map.set(key, p.accountName)
    })
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [posts])

  const allTags = useMemo(() => {
    const tags = new Set<string>()
    accounts.forEach(a => { if (a.tags) a.tags.forEach(t => tags.add(t)) })
    ytChannels.forEach(ch => { if (ch.tags) ch.tags.forEach(t => tags.add(t)) })
    return Array.from(tags).sort()
  }, [accounts, ytChannels])

  const tagPageIds = useMemo(() => {
    if (tagFilter === 'all') return null
    const ids = new Set<string>()
    accounts.forEach(a => {
      if (a.tags?.includes(tagFilter) && a.pageId) ids.add(String(a.pageId))
    })
    return ids
  }, [accounts, tagFilter])

  const tagYtChannelIds = useMemo(() => {
    if (tagFilter === 'all') return null
    const ids = new Set<string>()
    ytChannels.forEach(ch => {
      if (ch.tags?.includes(tagFilter)) ids.add(ch.id)
    })
    return ids
  }, [ytChannels, tagFilter])

  const categories = useMemo(() => {
    const cats = new Set<string>()
    posts.forEach(p => { if (p.category) cats.add(p.category) })
    return Array.from(cats).sort()
  }, [posts])

  const filteredPosts = useMemo(() => {
    return posts.filter(p => {
      if (accountFilter !== 'all') {
        const key = p.platform === 'yt' ? `yt_${p.youtubeChannelId}` : p.pageId
        if (key !== accountFilter) return false
      }
      if (statusFilter !== 'all') {
        const s = p.status || (p.published ? 'published' : 'pending')
        if (s !== statusFilter) return false
      }
      if (typeFilter !== 'all') {
        if (typeFilter === 'story' && p.postType !== 'story') return false
        if (typeFilter === 'reel' && !(p.postType !== 'story' && p.mediaType === 'video')) return false
        if (typeFilter === 'image' && !(p.postType !== 'story' && p.mediaType !== 'video')) return false
      }
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false
      if (tagPageIds || tagYtChannelIds) {
        const matchesPage = tagPageIds && tagPageIds.has(p.pageId)
        const matchesYt = tagYtChannelIds && p.youtubeChannelId && tagYtChannelIds.has(p.youtubeChannelId)
        if (!matchesPage && !matchesYt) return false
      }
      return true
    })
  }, [posts, accountFilter, statusFilter, typeFilter, categoryFilter, tagPageIds, tagYtChannelIds])

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days: Array<{ day: number; isCurrentMonth: boolean }> = []

    const prevDays = new Date(year, month, 0).getDate()
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: prevDays - i, isCurrentMonth: false })
    }
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ day: d, isCurrentMonth: true })
    }
    const remaining = 42 - days.length
    for (let d = 1; d <= remaining; d++) {
      days.push({ day: d, isCurrentMonth: false })
    }
    return days
  }, [year, month])

  const postsByDay = useMemo(() => {
    const map: Record<number, ScheduledPost[]> = {}
    for (const post of filteredPosts) {
      const d = new Date(post.scheduledAt)
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate()
        if (!map[day]) map[day] = []
        map[day].push(post)
      }
    }
    return map
  }, [filteredPosts, year, month])

  const selectedPosts = selectedDay ? (postsByDay[selectedDay] || []) : []
  const sortedDayPosts = [...selectedPosts].sort((a, b) => a.scheduledAt - b.scheduledAt)
  const totalDayPages = Math.ceil(sortedDayPosts.length / pageSize)
  const paginatedDayPosts = sortedDayPosts.slice(currentPage * pageSize, (currentPage + 1) * pageSize)

  const today = new Date()
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1))
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1))
  const goToday = () => { setCurrentDate(new Date()); setSelectedDay(today.getDate()); setCurrentPage(0) }

  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {/* Calendar grid */}
      <div className="lg:col-span-2">
        <div className="rounded-2xl border border-border/40 bg-card/50 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="border-b border-border/40 px-5 py-3.5 space-y-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg shrink-0" onClick={prevMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-bold capitalize tracking-tight">
                  {currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}
                </span>
                {!isCurrentMonth && (
                  <Button variant="outline" size="sm" className="h-6 text-[11px] rounded-lg border-border/40" onClick={goToday}>
                    Hoje
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg shrink-0" onClick={nextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap justify-center">
              {accountOptions.length > 1 && (
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger className="h-7 w-32 text-[11px] rounded-lg border-border/40"><SelectValue placeholder="Pagina" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas paginas</SelectItem>
                    {accountOptions.map(([pageId, name]) => (
                      <SelectItem key={pageId} value={pageId}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-7 w-28 text-[11px] rounded-lg border-border/40"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Status</SelectItem>
                  <SelectItem value="pending">Pendente</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                  <SelectItem value="failed">Falha</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-7 w-24 text-[11px] rounded-lg border-border/40"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tipo</SelectItem>
                  <SelectItem value="reel">Reel</SelectItem>
                  <SelectItem value="image">Imagem</SelectItem>
                  <SelectItem value="story">Story</SelectItem>
                </SelectContent>
              </Select>
              {categories.length > 0 && (
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-7 w-28 text-[11px] rounded-lg border-border/40"><SelectValue placeholder="Categoria" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Categoria</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {allTags.length > 0 && (
                <Select value={tagFilter} onValueChange={setTagFilter}>
                  <SelectTrigger className="h-7 w-24 text-[11px] rounded-lg border-border/40"><SelectValue placeholder="Tag" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tag</SelectItem>
                    {allTags.map(tag => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b border-border/30 bg-muted/20">
            {WEEKDAYS.map(d => (
              <div key={d} className="py-2.5 text-center text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {calendarDays.map((cell, i) => {
              const dayPosts = cell.isCurrentMonth ? (postsByDay[cell.day] || []) : []
              const isToday = cell.isCurrentMonth && isCurrentMonth && cell.day === today.getDate()
              const isSelected = cell.isCurrentMonth && cell.day === selectedDay

              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { if (cell.isCurrentMonth) { setSelectedDay(prev => prev === cell.day ? null : cell.day); setCurrentPage(0) } }}
                  className={cn(
                    'relative flex h-20 flex-col items-center p-1.5 text-xs transition-all duration-150 border-b border-r border-border/15',
                    !cell.isCurrentMonth && 'text-muted-foreground/40 bg-muted/5',
                    cell.isCurrentMonth && 'hover:bg-muted/30 cursor-pointer',
                    isToday && 'bg-primary/5',
                    isSelected && 'bg-primary/10 ring-1 ring-inset ring-primary/30',
                  )}
                >
                  <span className={cn(
                    'mb-1 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium transition-colors',
                    isToday && 'bg-primary text-primary-foreground font-bold shadow-sm',
                  )}>
                    {cell.day}
                  </span>
                  {dayPosts.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-0.5">
                      {dayPosts.slice(0, 4).map((post, j) => {
                        const isFailed = post.status === 'failed'
                        const isPublished = post.status === 'published' || post.published
                        const isYt = post.platform === 'yt'
                        const isStory = post.postType === 'story'
                        const isReel = !isStory && post.mediaType === 'video'
                        const color = isFailed ? 'text-destructive' : isYt ? 'text-red-500' : isStory ? 'text-purple-600 dark:text-purple-400' : isReel ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
                        const Icon = isYt ? Youtube : isStory ? Smartphone : isReel ? Film : Image
                        return (
                          <Icon
                            key={j}
                            className={cn(
                              'h-3 w-3',
                              color,
                              !isPublished && !isFailed && 'opacity-70',
                            )}
                          />
                        )
                      })}
                      {dayPosts.length > 4 && (
                        <span className="text-[7px] font-bold text-muted-foreground">+{dayPosts.length - 4}</span>
                      )}
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 border-t border-border/30 px-5 py-2.5 bg-muted/10">
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Film className="h-3 w-3 text-orange-600 dark:text-orange-400" /> Reel
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Image className="h-3 w-3 text-blue-600 dark:text-blue-400" /> Imagem
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Smartphone className="h-3 w-3 text-purple-600 dark:text-purple-400" /> Story
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Youtube className="h-3 w-3 text-red-500" /> YouTube
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <div className="h-2 w-2 rounded-full bg-destructive" /> Falha
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Film className="h-3 w-3 text-muted-foreground" /> Publicado
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Film className="h-3 w-3 text-muted-foreground" /> Pendente
            </div>
          </div>
        </div>
      </div>

      {/* Day detail panel */}
      <div>
        <div className="sticky top-4 rounded-2xl border border-border/40 bg-card/80 backdrop-blur-sm p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary/20 to-primary/5">
              <CalendarDays className="h-4 w-4 text-primary" />
            </div>
            <span className="text-sm font-bold">
              {selectedDay
                ? `${selectedDay} de ${currentDate.toLocaleString('pt-BR', { month: 'long' })}`
                : 'Selecione um dia'
              }
            </span>
            {selectedPosts.length > 0 && (
              <div className="ml-auto flex items-center gap-1.5">
                <span className="text-[11px] font-medium text-muted-foreground">{selectedPosts.length} post{selectedPosts.length !== 1 && 's'}</span>
                {selectedPosts.length > 4 && (
                  <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setCurrentPage(0) }}>
                    <SelectTrigger className="h-6 w-14 text-[11px] rounded-lg border-border/40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>

          {!selectedDay ? (
            <div className="flex flex-col items-center py-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/30 mb-3">
                <CalendarDays className="h-7 w-7 text-muted-foreground/40" />
              </div>
              <p className="text-xs text-muted-foreground">Clique em um dia para ver os posts</p>
            </div>
          ) : selectedPosts.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/30 mb-3">
                <CalendarDays className="h-7 w-7 text-muted-foreground/40" />
              </div>
              <p className="text-xs text-muted-foreground">Nenhum post neste dia</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {paginatedDayPosts.map(post => (
                <PostCard key={post.id} post={post} onEdit={onEdit} compact />
              ))}
              {totalDayPages > 1 && (
                <div className="flex items-center justify-between pt-2.5 border-t border-border/30">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px] gap-1 rounded-lg"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    <ChevronLeft className="h-3 w-3" /> Anterior
                  </Button>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {currentPage + 1} / {totalDayPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[11px] gap-1 rounded-lg"
                    disabled={currentPage >= totalDayPages - 1}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Proximo <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {clearCounts.total > 0 && (
            <div className="mt-4 border-t border-border/30 pt-4 relative" ref={clearRef}>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-1.5 text-[11px] rounded-xl border-border/40 text-muted-foreground hover:text-destructive hover:border-destructive/30 transition-colors"
                disabled={clearPublished.isPending}
                onClick={() => {
                  setClearOpen(!clearOpen)
                  if (!clearOpen) {
                    setClearStatuses({ published: true, failed: true, pending: true })
                    setClearTypes({ photo: true, clip: true, long_video: true, story: true })
                  }
                }}
              >
                <Trash2 className="h-3 w-3" />
                {clearPublished.isPending ? 'Limpando...' : `Limpar historico (${clearCounts.total})`}
                <ChevronDown className="h-3 w-3" />
              </Button>
              {clearOpen && (
                <div className="absolute bottom-full left-0 right-0 z-50 mb-1.5 rounded-xl border border-border/40 bg-popover/95 backdrop-blur-md p-3 shadow-xl slide-up">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Status</p>
                  {([
                    { key: 'published', label: 'Publicados', color: 'text-green-500' },
                    { key: 'failed', label: 'Com Falha', color: 'text-red-500' },
                    { key: 'pending', label: 'Pendentes', color: 'text-yellow-500' },
                  ] as const).filter(s => clearCounts.byStatus[s.key] > 0).map(s => (
                    <label key={s.key} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                      <input
                        type="checkbox"
                        checked={clearStatuses[s.key]}
                        onChange={() => setClearStatuses(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                        className="h-3.5 w-3.5 rounded border-border/60 accent-primary"
                      />
                      <span className={`flex-1 ${s.color}`}>{s.label}</span>
                      <span className="text-muted-foreground tabular-nums">({clearCounts.byStatus[s.key]})</span>
                    </label>
                  ))}
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 mt-3">Tipo</p>
                  {([
                    { key: 'photo', label: 'Fotos' },
                    { key: 'clip', label: 'Cortes' },
                    { key: 'long_video', label: 'Videos Longos' },
                    { key: 'story', label: 'Stories' },
                  ] as const).filter(t => clearCounts.byType[t.key] > 0).map(tp => (
                    <label key={tp.key} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                      <input
                        type="checkbox"
                        checked={clearTypes[tp.key]}
                        onChange={() => setClearTypes(prev => ({ ...prev, [tp.key]: !prev[tp.key] }))}
                        className="h-3.5 w-3.5 rounded border-border/60 accent-primary"
                      />
                      <span className="flex-1">{tp.label}</span>
                      <span className="text-muted-foreground tabular-nums">({clearCounts.byType[tp.key]})</span>
                    </label>
                  ))}
                  <button
                    className="w-full mt-3 rounded-lg bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors disabled:opacity-40"
                    disabled={(!Object.values(clearStatuses).some(Boolean) || !Object.values(clearTypes).some(Boolean)) || clearPublished.isPending}
                    onClick={() => {
                      const activeStatuses = Object.entries(clearStatuses).filter(([, v]) => v).map(([k]) => k)
                      const activeTypes = Object.entries(clearTypes).filter(([, v]) => v).map(([k]) => k)
                      const statusLabels = activeStatuses.map(s => s === 'published' ? 'publicados' : s === 'failed' ? 'com falha' : 'pendentes').join(', ')
                      if (!confirm(`Limpar posts ${statusLabels}?`)) return
                      setClearOpen(false)
                      clearPublished.mutate({ statuses: activeStatuses, types: activeTypes }, {
                        onSuccess: (data) => toast.success(`${data.removed} post(s) removidos`),
                        onError: () => toast.error('Erro ao limpar'),
                      })
                    }}
                  >
                    {clearPublished.isPending ? 'Limpando...' : 'Limpar Selecionados'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
